import { useState, useEffect, useRef } from 'react';
import { supabase } from './lib/supabase';
import {
  LayoutDashboard, Store, Package, MessageSquare,
  Plus, Trash2, Edit2, Save, X,
  AlertCircle, CheckCircle, Bot, ShoppingBag,
  Users, MessageCircle, Upload, Image
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';

const STORE_ID = process.env.REACT_APP_STORE_ID;

const s = {
  root: { fontFamily: "'DM Sans', sans-serif", background: '#f8f9fb', minHeight: '100vh', color: '#111' },
  sidebar: { position: 'fixed', top: 0, left: 0, bottom: 0, width: 220, background: '#fff', borderRight: '1px solid #eee', display: 'flex', flexDirection: 'column', zIndex: 100 },
  logo: { padding: '24px 24px 20px', borderBottom: '1px solid #f0f0f0' },
  logoText: { fontSize: 22, fontWeight: 600, color: '#111', letterSpacing: '-0.5px' },
  nav: { padding: '16px 12px', flex: 1 },
  navItem: (a) => ({ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 8, cursor: 'pointer', marginBottom: 2, fontSize: 14, fontWeight: a ? 600 : 400, color: a ? '#2563eb' : '#555', background: a ? '#eff6ff' : 'transparent' }),
  main: { marginLeft: 220, padding: '32px 36px' },
  pageTitle: { fontSize: 24, fontWeight: 600, color: '#111', margin: 0, letterSpacing: '-0.3px' },
  pageSub: { fontSize: 14, color: '#888', marginTop: 4, marginBottom: 28 },
  card: { background: '#fff', borderRadius: 12, border: '1px solid #eee', padding: 24, marginBottom: 20 },
  statGrid: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24 },
  statCard: { background: '#fff', borderRadius: 12, border: '1px solid #eee', padding: '20px 24px' },
  statVal: { fontSize: 28, fontWeight: 600, color: '#111', margin: '8px 0 4px' },
  statLbl: { fontSize: 13, color: '#888' },
  statIcon: (bg) => ({ width: 36, height: 36, borderRadius: 8, background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }),
  btn: (v = 'primary') => ({ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 16px', borderRadius: 8, fontSize: 14, fontWeight: 500, cursor: 'pointer', border: 'none', background: v === 'primary' ? '#2563eb' : v === 'danger' ? '#fee2e2' : '#f4f4f5', color: v === 'primary' ? '#fff' : v === 'danger' ? '#dc2626' : '#333' }),
  input: { width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid #e5e7eb', fontSize: 14, color: '#111', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit', background: '#fff' },
  label: { fontSize: 13, fontWeight: 500, color: '#444', marginBottom: 6, display: 'block' },
  formGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { textAlign: 'left', padding: '10px 16px', fontSize: 12, fontWeight: 600, color: '#888', borderBottom: '1px solid #f0f0f0', textTransform: 'uppercase', letterSpacing: '0.5px' },
  td: { padding: '14px 16px', fontSize: 14, color: '#333', borderBottom: '1px solid #f9f9f9' },
  badge: (i) => ({ display: 'inline-block', padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 500, background: i === 'ORDER' ? '#dcfce7' : i === 'COMPLAINT' ? '#fee2e2' : i === 'OBJECTION' ? '#fef3c7' : '#eff6ff', color: i === 'ORDER' ? '#16a34a' : i === 'COMPLAINT' ? '#dc2626' : i === 'OBJECTION' ? '#d97706' : '#2563eb' }),
  chBadge: (c) => ({ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 500, background: c === 'whatsapp' ? '#dcfce7' : '#fce7f3', color: c === 'whatsapp' ? '#16a34a' : '#db2777' }),
  toast: (t) => ({ position: 'fixed', bottom: 24, right: 24, background: t === 'success' ? '#111' : '#dc2626', color: '#fff', padding: '12px 20px', borderRadius: 10, fontSize: 14, fontWeight: 500, zIndex: 999, display: 'flex', alignItems: 'center', gap: 8, boxShadow: '0 4px 20px rgba(0,0,0,0.15)' }),
  empty: { textAlign: 'center', padding: '48px 0', color: '#aaa', fontSize: 14 },
  uploadBox: { border: '2px dashed #e5e7eb', borderRadius: 8, padding: '20px', textAlign: 'center', cursor: 'pointer', transition: 'all 0.2s', background: '#fafafa' },
  productImg: { width: 48, height: 48, borderRadius: 8, objectFit: 'cover', background: '#f4f4f5' },
};

function Toast({ message, type, onClose }) {
  useEffect(() => { const t = setTimeout(onClose, 3000); return () => clearTimeout(t); }, [onClose]);
  return <div style={s.toast(type)}>{type === 'success' ? <CheckCircle size={16} /> : <AlertCircle size={16} />}{message}</div>;
}

function Analytics({ storeId }) {
  const [stats, setStats] = useState({ total: 0, orders: 0, complaints: 0, whatsapp: 0 });
  const [chart, setChart] = useState([]);
  const [intents, setIntents] = useState([]);

  useEffect(() => {
    supabase.from('conversations').select('*').eq('store_id', storeId).order('created_at', { ascending: false })
      .then(({ data }) => {
        if (!data) return;
        setStats({ total: data.length, orders: data.filter(d => d.intent === 'ORDER').length, complaints: data.filter(d => d.intent === 'COMPLAINT').length, whatsapp: data.filter(d => d.channel === 'whatsapp').length });
        const days = {};
        for (let i = 6; i >= 0; i--) { const d = new Date(); d.setDate(d.getDate() - i); days[d.toLocaleDateString('en-IN', { weekday: 'short' })] = 0; }
        data.forEach(c => { const k = new Date(c.created_at).toLocaleDateString('en-IN', { weekday: 'short' }); if (days[k] !== undefined) days[k]++; });
        setChart(Object.entries(days).map(([name, count]) => ({ name, count })));
        const iv = {};
        data.forEach(c => { iv[c.intent] = (iv[c.intent] || 0) + 1; });
        setIntents(Object.entries(iv).map(([name, value]) => ({ name, value })));
      });
  }, [storeId]);

  return (
    <div>
      <h1 style={s.pageTitle}>Dashboard</h1>
      <p style={s.pageSub}>Your AI sales agent performance overview</p>
      <div style={s.statGrid}>
        {[
          { label: 'Total Conversations', value: stats.total, icon: <MessageCircle size={18} color="#2563eb" />, bg: '#eff6ff' },
          { label: 'Orders Captured', value: stats.orders, icon: <ShoppingBag size={18} color="#16a34a" />, bg: '#dcfce7' },
          { label: 'WhatsApp Chats', value: stats.whatsapp, icon: <Users size={18} color="#16a34a" />, bg: '#dcfce7' },
          { label: 'Complaints', value: stats.complaints, icon: <AlertCircle size={18} color="#dc2626" />, bg: '#fee2e2' },
        ].map((st, i) => (
          <div key={i} style={s.statCard}>
            <div style={s.statIcon(st.bg)}>{st.icon}</div>
            <div style={s.statVal}>{st.value}</div>
            <div style={s.statLbl}>{st.label}</div>
          </div>
        ))}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr', gap: 20 }}>
        <div style={s.card}>
          <h3 style={{ margin: '0 0 20px', fontSize: 15, fontWeight: 600 }}>Messages This Week</h3>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={chart}><XAxis dataKey="name" tick={{ fontSize: 12 }} axisLine={false} tickLine={false} /><YAxis tick={{ fontSize: 12 }} axisLine={false} tickLine={false} /><Tooltip /><Bar dataKey="count" fill="#2563eb" radius={[4, 4, 0, 0]} /></BarChart>
          </ResponsiveContainer>
        </div>
        <div style={s.card}>
          <h3 style={{ margin: '0 0 20px', fontSize: 15, fontWeight: 600 }}>Intent Breakdown</h3>
          {intents.length === 0 ? <div style={s.empty}>No data yet</div> : intents.map((d, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
              <span style={s.badge(d.name)}>{d.name}</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ width: 80, height: 6, background: '#f0f0f0', borderRadius: 3 }}><div style={{ width: `${Math.min((d.value / stats.total) * 100, 100)}%`, height: '100%', background: '#2563eb', borderRadius: 3 }} /></div>
                <span style={{ fontSize: 13, color: '#888', width: 24 }}>{d.value}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function StoreSetup({ storeId, onToast }) {
  const [store, setStore] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { supabase.from('stores').select('*').eq('id', storeId).single().then(({ data }) => data && setStore(data)); }, [storeId]);
  async function save() { setSaving(true); await supabase.from('stores').update(store).eq('id', storeId); setSaving(false); onToast('Saved!', 'success'); }
  if (!store) return <div style={s.empty}>Loading...</div>;
  return (
    <div>
      <h1 style={s.pageTitle}>Store Setup</h1>
      <p style={s.pageSub}>Configure your store details</p>
      <div style={s.card}>
        <h3 style={{ margin: '0 0 20px', fontSize: 15, fontWeight: 600 }}>Store Information</h3>
        <div style={s.formGrid}>
          {[['Store Name', 'name'], ['WhatsApp Phone ID', 'whatsapp_phone_id'], ['Instagram Page ID', 'instagram_page_id'], ['Currency', 'currency']].map(([label, key]) => (
            <div key={key}><label style={s.label}>{label}</label><input style={s.input} value={store[key] || ''} onChange={e => setStore({ ...store, [key]: e.target.value })} /></div>
          ))}
        </div>
        <div style={{ marginTop: 16 }}><label style={s.label}>Greeting Message</label><textarea style={{ ...s.input, minHeight: 80, resize: 'vertical' }} value={store.greeting_message || ''} onChange={e => setStore({ ...store, greeting_message: e.target.value })} /></div>
        <div style={{ marginTop: 20 }}><button style={s.btn('primary')} onClick={save}><Save size={15} />{saving ? 'Saving...' : 'Save Changes'}</button></div>
      </div>
      <div style={s.card}>
        <h3 style={{ margin: '0 0 16px', fontSize: 15, fontWeight: 600 }}>AI Agent Status</h3>
        {['Intent Agent', 'RAG Agent', 'Sales Agent', 'Closer Agent', 'Order Agent', 'Escalation Agent'].map((agent, i, arr) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 0', borderBottom: i < arr.length - 1 ? '1px solid #f5f5f5' : 'none' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}><Bot size={16} color="#2563eb" /><span style={{ fontSize: 14 }}>{agent}</span></div>
            <span style={{ fontSize: 12, background: '#dcfce7', color: '#16a34a', padding: '3px 10px', borderRadius: 20, fontWeight: 500 }}>Active</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function Products({ storeId, onToast }) {
  const [products, setProducts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [adding, setAdding] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({ name: '', price: '', description: '', stock: '', is_active: true, image_url: '' });
  const fileRef = useRef();

  const load = () => supabase.from('products').select('*').eq('store_id', storeId).order('created_at').then(({ data }) => setProducts(data || []));
  useEffect(() => { load(); }, [storeId]);

  async function uploadImage(file) {
    if (!file) return null;
    setUploading(true);
    try {
      const ext = file.name.split('.').pop();
      const fileName = `${storeId}/${Date.now()}.${ext}`;
      const { error } = await supabase.storage
        .from('product-images')
        .upload(fileName, file, { upsert: true });
      if (error) throw error;
      const { data } = supabase.storage.from('product-images').getPublicUrl(fileName);
      return data.publicUrl;
    } catch (err) {
      onToast('Image upload failed: ' + err.message, 'error');
      return null;
    } finally {
      setUploading(false);
    }
  }

  async function handleFileChange(e) {
    const file = e.target.files[0];
    if (!file) return;
    const url = await uploadImage(file);
    if (url) {
      setForm(f => ({ ...f, image_url: url }));
      onToast('Image uploaded!', 'success');
    }
  }

  async function save() {
    const payload = { ...form, price: parseFloat(form.price), stock: parseInt(form.stock) };
    if (editing) { await supabase.from('products').update(payload).eq('id', editing); onToast('Updated!', 'success'); }
    else { await supabase.from('products').insert({ ...payload, store_id: storeId }); onToast('Added!', 'success'); }
    setEditing(null); setAdding(false);
    setForm({ name: '', price: '', description: '', stock: '', is_active: true, image_url: '' });
    load();
  }

  async function remove(id, imageUrl) {
    if (imageUrl) {
      const path = imageUrl.split('/product-images/')[1];
      if (path) await supabase.storage.from('product-images').remove([path]);
    }
    await supabase.from('products').delete().eq('id', id);
    onToast('Deleted', 'success'); load();
  }

  function startEdit(p) {
    setEditing(p.id);
    setForm({ name: p.name, price: p.price, description: p.description, stock: p.stock, is_active: p.is_active, image_url: p.image_url || '' });
    setAdding(true);
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 }}>
        <h1 style={s.pageTitle}>Product Catalog</h1>
        <button style={s.btn('primary')} onClick={() => { setAdding(true); setEditing(null); setForm({ name: '', price: '', description: '', stock: '', is_active: true, image_url: '' }); }}><Plus size={15} />Add Product</button>
      </div>
      <p style={s.pageSub}>{products.length} products in your catalog</p>

      {adding && (
        <div style={s.card}>
          <h3 style={{ margin: '0 0 20px', fontSize: 15, fontWeight: 600 }}>{editing ? 'Edit Product' : 'New Product'}</h3>
          <div style={s.formGrid}>
            {[['Product Name', 'name', 'text'], ['Price (₹)', 'price', 'number'], ['Stock', 'stock', 'number']].map(([label, key, type]) => (
              <div key={key}><label style={s.label}>{label}</label><input style={s.input} type={type} value={form[key]} onChange={e => setForm({ ...form, [key]: e.target.value })} /></div>
            ))}
          </div>
          <div style={{ marginTop: 16 }}>
            <label style={s.label}>Description</label>
            <textarea style={{ ...s.input, minHeight: 70, resize: 'vertical' }} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} />
          </div>

          {/* Image Upload */}
          <div style={{ marginTop: 16 }}>
            <label style={s.label}>Product Image</label>
            <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleFileChange} />
            {form.image_url ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <img src={form.image_url} alt="product" style={{ width: 80, height: 80, borderRadius: 8, objectFit: 'cover', border: '1px solid #eee' }} />
                <div>
                  <button style={{ ...s.btn('secondary'), marginBottom: 8, display: 'flex' }} onClick={() => fileRef.current.click()}>
                    <Upload size={14} /> Change Image
                  </button>
                  <button style={{ ...s.btn('danger'), display: 'flex' }} onClick={() => setForm(f => ({ ...f, image_url: '' }))}>
                    <X size={14} /> Remove
                  </button>
                </div>
              </div>
            ) : (
              <div style={{ ...s.uploadBox, borderColor: uploading ? '#2563eb' : '#e5e7eb' }} onClick={() => fileRef.current.click()}>
                {uploading ? (
                  <div style={{ color: '#2563eb', fontSize: 14 }}>Uploading...</div>
                ) : (
                  <>
                    <Image size={24} color="#aaa" style={{ marginBottom: 8 }} />
                    <div style={{ fontSize: 14, color: '#888' }}>Click to upload product image</div>
                    <div style={{ fontSize: 12, color: '#bbb', marginTop: 4 }}>JPG, PNG up to 5MB</div>
                  </>
                )}
              </div>
            )}
          </div>

          <div style={{ marginTop: 16, display: 'flex', gap: 10 }}>
            <button style={s.btn('primary')} onClick={save} disabled={uploading}><Save size={15} />Save</button>
            <button style={s.btn('secondary')} onClick={() => { setAdding(false); setEditing(null); }}><X size={15} />Cancel</button>
          </div>
        </div>
      )}

      <div style={s.card}>
        <table style={s.table}>
          <thead><tr>{['Image', 'Product', 'Price', 'Stock', 'Status', 'Actions'].map(h => <th key={h} style={s.th}>{h}</th>)}</tr></thead>
          <tbody>
            {products.length === 0
              ? <tr><td colSpan={6} style={{ ...s.td, textAlign: 'center', color: '#aaa' }}>No products yet</td></tr>
              : products.map(p => (
                <tr key={p.id}>
                  <td style={s.td}>
                    {p.image_url
                      ? <img src={p.image_url} alt={p.name} style={s.productImg} />
                      : <div style={{ ...s.productImg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Image size={20} color="#ccc" /></div>
                    }
                  </td>
                  <td style={s.td}>
                    <div style={{ fontWeight: 500 }}>{p.name}</div>
                    <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>{(p.description || '').substring(0, 50)}...</div>
                  </td>
                  <td style={s.td}>₹{parseFloat(p.price).toLocaleString('en-IN')}</td>
                  <td style={s.td}>{p.stock} units</td>
                  <td style={s.td}>
                    <span style={{ fontSize: 12, padding: '3px 10px', borderRadius: 20, fontWeight: 500, background: p.is_active ? '#dcfce7' : '#f4f4f5', color: p.is_active ? '#16a34a' : '#888' }}>
                      {p.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td style={s.td}>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button style={{ ...s.btn('secondary'), padding: '6px 10px' }} onClick={() => startEdit(p)}><Edit2 size={13} /></button>
                      <button style={{ ...s.btn('danger'), padding: '6px 10px' }} onClick={() => remove(p.id, p.image_url)}><Trash2 size={13} /></button>
                    </div>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Conversations({ storeId }) {
  const [convos, setConvos] = useState([]);
  const [selected, setSelected] = useState(null);
  const [filter, setFilter] = useState('all');
  useEffect(() => { supabase.from('conversations').select('*').eq('store_id', storeId).order('created_at', { ascending: false }).limit(100).then(({ data }) => setConvos(data || [])); }, [storeId]);
  const filtered = filter === 'all' ? convos : convos.filter(c => c.channel === filter || c.intent === filter);
  return (
    <div>
      <h1 style={s.pageTitle}>Conversations</h1>
      <p style={s.pageSub}>{convos.length} total conversations</p>
      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        {['all', 'whatsapp', 'instagram', 'ORDER', 'COMPLAINT'].map(f => (
          <button key={f} style={{ ...s.btn(filter === f ? 'primary' : 'secondary'), padding: '7px 14px', fontSize: 13 }} onClick={() => setFilter(f)}>
            {f === 'all' ? 'All' : f.charAt(0) + f.slice(1).toLowerCase()}
          </button>
        ))}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: selected ? '1fr 1fr' : '1fr', gap: 20 }}>
        <div style={s.card}>
          <table style={s.table}>
            <thead><tr>{['Channel', 'Message', 'Intent', 'Time'].map(h => <th key={h} style={s.th}>{h}</th>)}</tr></thead>
            <tbody>
              {filtered.length === 0
                ? <tr><td colSpan={4} style={{ ...s.td, textAlign: 'center', color: '#aaa' }}>No conversations yet</td></tr>
                : filtered.map(c => (
                  <tr key={c.id} onClick={() => setSelected(c)} style={{ cursor: 'pointer', background: selected?.id === c.id ? '#f8faff' : 'transparent' }}>
                    <td style={s.td}><span style={s.chBadge(c.channel)}>{c.channel}</span></td>
                    <td style={s.td}><div style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 13 }}>{c.customer_message}</div></td>
                    <td style={s.td}><span style={s.badge(c.intent)}>{c.intent}</span></td>
                    <td style={{ ...s.td, fontSize: 12, color: '#888' }}>{new Date(c.created_at).toLocaleString('en-IN', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
        {selected && (
          <div style={s.card}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>Conversation Detail</h3>
              <button style={{ ...s.btn('secondary'), padding: '6px 10px' }} onClick={() => setSelected(null)}><X size={14} /></button>
            </div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}><span style={s.chBadge(selected.channel)}>{selected.channel}</span><span style={s.badge(selected.intent)}>{selected.intent}</span></div>
            <div style={{ fontSize: 12, color: '#888', marginBottom: 16 }}>{new Date(selected.created_at).toLocaleString('en-IN')}</div>
            <div style={{ background: '#f8f9fb', borderRadius: 8, padding: 14, marginBottom: 12 }}>
              <div style={{ fontSize: 12, color: '#888', marginBottom: 6 }}>Customer</div>
              <div style={{ fontSize: 14 }}>{selected.customer_message}</div>
            </div>
            <div style={{ background: '#eff6ff', borderRadius: 8, padding: 14 }}>
              <div style={{ fontSize: 12, color: '#2563eb', marginBottom: 6 }}>Sellar AI</div>
              <div style={{ fontSize: 14, whiteSpace: 'pre-wrap' }}>{selected.bot_reply}</div>
            </div>
            {selected.products_matched > 0 && <div style={{ marginTop: 12, fontSize: 12, color: '#888' }}>🎯 {selected.products_matched} product(s) matched</div>}
          </div>
        )}
      </div>
    </div>
  );
}

export default function App() {
  const [page, setPage] = useState('analytics');
  const [toast, setToast] = useState(null);
  const navItems = [
    { id: 'analytics', label: 'Dashboard', icon: <LayoutDashboard size={16} /> },
    { id: 'store', label: 'Store Setup', icon: <Store size={16} /> },
    { id: 'products', label: 'Products', icon: <Package size={16} /> },
    { id: 'conversations', label: 'Conversations', icon: <MessageSquare size={16} /> },
  ];
  return (
    <div style={s.root}>
      <div style={s.sidebar}>
        <div style={s.logo}>
          <div style={s.logoText}>Sellar<span style={{ color: '#2563eb' }}>.</span></div>
          <div style={{ fontSize: 12, color: '#aaa', marginTop: 2 }}>AI Sales Dashboard</div>
        </div>
        <nav style={s.nav}>
          {navItems.map(item => (
            <div key={item.id} style={s.navItem(page === item.id)} onClick={() => setPage(item.id)}>
              {item.icon}{item.label}
            </div>
          ))}
        </nav>
        <div style={{ padding: '16px 24px', borderTop: '1px solid #f0f0f0' }}>
          <div style={{ fontSize: 12, color: '#aaa' }}>Powered by Claude AI</div>
          <div style={{ fontSize: 11, color: '#ccc', marginTop: 2 }}>v1.0.0</div>
        </div>
      </div>
      <main style={s.main}>
        {page === 'analytics' && <Analytics storeId={STORE_ID} />}
        {page === 'store' && <StoreSetup storeId={STORE_ID} onToast={(m, t) => setToast({ message: m, type: t })} />}
        {page === 'products' && <Products storeId={STORE_ID} onToast={(m, t) => setToast({ message: m, type: t })} />}
        {page === 'conversations' && <Conversations storeId={STORE_ID} />}
      </main>
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
}
